import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Checkbox,
  Container,
  Dimmer,
  Form,
  Grid,
  Icon,
  Label,
  Loader,
  Message,
  Modal,
  Segment,
  Table,
  Dropdown,
  Popup,
  List,
} from 'semantic-ui-react';
import { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useStockInconsistencySummary } from '../context/StockInconsistencySummaryContext';
import { stockyApi } from '../misc/StockyApi';
import { pageBodyStyle, pageFrameStyle, pageShellStyle, sectionTitleStyle, surfaceCardStyle } from '../misc/pageStyles';
import {
  ML_STALE_VARIATION_MAPPING_CODE,
  getStockResolutionErrorCode,
  hasLiveChannelStock,
  isStaleVariationMappingIssue,
  requiresManualVariationRemap,
  summarizeBulkResolutionResponses,
  usesStockSourceResolutionFlow,
} from './stockInconsistencyResolution.utils';

const typeLabelByType = {
  stock_mismatch: 'Diferencia de stock',
  warehouse_not_found: 'Publicación sin registrar',
  listing_not_found: 'Publicación no encontrada',
  insufficient_stock: 'Stock insuficiente',
  sale_without_stock: 'Venta sin stock',
  stock_record_missing: 'Publicación sin registrar',
  channel_sync_failed: 'Sync de canal fallido',
  unknown_stock_error: 'Error desconocido',
};

const typeFilterOptions = [
  { key: 'all', text: 'Todos', value: '' },
  { key: 'stock_mismatch', text: 'Diferencia de stock', value: 'stock_mismatch' },
  { key: 'publication_missing', text: 'Publicación sin registrar', value: 'publication_missing' },
  { key: 'listing_not_found', text: 'Publicación no encontrada', value: 'listing_not_found' },
  { key: 'sale_without_stock', text: 'Ventas sin stock', value: 'sale_without_stock' },
  { key: 'fanout_failed', text: 'Error de canal al actualizar', value: 'fanout_failed' },
  { key: 'channel_sync_failed', text: 'Sync de canal fallido', value: 'channel_sync_failed' },
  { key: 'unknown_stock_error', text: 'Error desconocido', value: 'unknown_stock_error' },
];

const expandTypeFilters = (types) => {
  const normalized = Array.from(new Set((types || []).filter(Boolean)));
  return normalized.flatMap((value) =>
    value === 'publication_missing'
      ? ['stock_record_missing', 'warehouse_not_found']
      : [value],
  );
};

const compressTypeFilters = (types) => {
  const normalized = Array.from(new Set((types || []).filter(Boolean)));
  const hasMissingPair =
    normalized.includes('stock_record_missing') && normalized.includes('warehouse_not_found');
  if (!hasMissingPair) return normalized;
  return normalized.filter((value) => value !== 'stock_record_missing' && value !== 'warehouse_not_found').concat('publication_missing');
};

const severityOptions = [
  { key: 'critical', text: 'Crítico', value: 'critical' },
  { key: 'warning', text: 'Advertencia', value: 'warning' },
  { key: 'info', text: 'Info', value: 'info' },
];

const statusOptions = [
  { key: 'open', text: 'Abierto', value: 'open' },
  { key: 'in_progress', text: 'En progreso', value: 'in_progress' },
  { key: 'resolved', text: 'Resuelto', value: 'resolved' },
  { key: 'ignored', text: 'Ignorado', value: 'ignored' },
];

const channelOptions = [
  { key: 'all', text: 'Todos', value: '' },
  { key: 'ml', text: 'Mercado Libre', value: 'mercado_libre' },
  { key: 'tn', text: 'Tienda Nube', value: 'tienda_nube' },
  { key: 'mercurycom', text: 'MercuryCom', value: 'mercurycom' },
];

const defaultFilters = { type: [], severity: [], status: ['open', 'in_progress'], channel: '', sku: '', search: '' };
const confirmRequiredActions = new Set(['update_channel_from_mercury', 'use_channel_stock_as_mercury', 'unlink_listing', 'create_missing_stock_record', 'reimport_listing_from_channel']);

const actionByType = {
  stock_record_missing: { key: 'create_missing_stock_record', label: 'Registrar' },
  warehouse_not_found: { key: 'create_missing_stock_record', label: 'Registrar' },
  stock_mismatch: { key: 'update_channel_from_mercury', label: 'Alinear stock' },
  listing_not_found: { key: 'reimport_listing_from_channel', label: 'Releer' },
  fanout_failed: { key: 'update_channel_from_mercury', label: 'Resolver' },
  insufficient_stock: { key: 'update_channel_from_mercury', label: 'Resolver' },
  sale_without_stock: { key: 'ignore', label: 'Revisada, sin modificar stock' },
};

function formatDateTime(value) {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return new Intl.DateTimeFormat('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }).format(d);
}

function formatRelative(value) {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  const diffMs = d.getTime() - Date.now();
  const minutes = Math.round(diffMs / 60000);
  const hours = Math.round(diffMs / 3600000);
  const days = Math.round(diffMs / 86400000);
  const f = new Intl.RelativeTimeFormat('es', { numeric: 'auto' });
  if (Math.abs(minutes) < 60) return f.format(minutes, 'minute');
  if (Math.abs(hours) < 24) return f.format(hours, 'hour');
  return f.format(days, 'day');
}

const getChannelLabel = (c) => (c === 'mercado_libre' ? 'Mercado Libre' : c === 'tienda_nube' ? 'Tienda Nube' : c === 'mercurycom' ? 'MercuryCom' : c || '-');
const getSeverityColor = (s) => (s === 'critical' ? 'red' : s === 'warning' ? 'yellow' : 'grey');
const getSeverityLabel = (s) => (s === 'critical' ? 'Crítico' : s === 'warning' ? 'Advertencia' : 'Info');
const getStatusLabel = (s) => (s === 'open' ? 'Abierto' : s === 'in_progress' ? 'En progreso' : s === 'resolved' ? 'Resuelto' : s === 'ignored' ? 'Ignorado' : s || '-');
const extractApiErrorMessage = (error, fallbackMessage) => error?.response?.data?.message || fallbackMessage;
const buildResolutionErrorToast = ({ issue, action, error, bulkCount }) => {
  const apiMessage = extractApiErrorMessage(error, 'No pudimos completar la acción.');
  const actionLabel =
    action === 'reimport_listing_from_channel'
      ? 'releer la publicación'
      : action === 'create_missing_stock_record'
        ? 'registrar la publicación'
        : action === 'update_channel_from_mercury'
          ? 'actualizar el canal'
          : action === 'use_channel_stock_as_mercury'
            ? 'actualizar MercuryCom'
            : action === 'ignore'
              ? 'ignorar la alerta'
              : 'resolver la alerta';

  let reason = apiMessage;
  if (apiMessage.includes('datos suficientes para reimportar la publicacion')) {
    reason =
      'Falta identificar la cuenta del canal para esta alerta. Necesitamos el seller o store asociado para volver a importar la publicación.';
  } else if (apiMessage.includes('No token found for seller')) {
    reason =
      'No encontramos una cuenta conectada para esta publicación. Revisá las cuentas conectadas del canal antes de reintentar.';
  } else if (apiMessage.includes('No pudimos obtener el stock actual del canal')) {
    reason =
      'El canal no devolvió stock en este momento. Reintentá cuando Mercado Libre o Tienda Nube respondan nuevamente.';
  } else if (apiMessage.includes('No hay publicacion asociada')) {
    reason =
      'La alerta no tiene una publicación local vinculada para ejecutar esta acción automáticamente.';
  }

  return {
    kind: 'negative',
    title: bulkCount > 1 ? 'No pudimos completar la acción masiva' : 'No pudimos completar la acción',
    lines: [
      bulkCount > 1
        ? `Intentamos ${actionLabel} en ${bulkCount} alertas, pero la operación falló.`
        : `Intentamos ${actionLabel}${issue?.listingTitle ? ` para ${issue.listingTitle}` : ''}, pero la operación falló.`,
      reason,
    ],
  };
};
const getTypeLabel = (type, channel, metadata) => {
  if (metadata?.resolutionBlockerCode === ML_STALE_VARIATION_MAPPING_CODE) {
    return metadata?.staleVariationRecoveryMode === 'manual_remap_required' ||
      (Array.isArray(metadata?.staleVariationMapping?.remoteVariationIds) &&
        metadata.staleVariationMapping.remoteVariationIds.length > 0)
      ? 'Variación requiere revisión manual'
      : 'Referencia de variación desactualizada';
  }
  if (type === 'fanout_failed') {
    return `Error de ${getChannelLabel(channel)} al actualizar`;
  }
  if (type === 'listing_not_found' && metadata?.reason === 'listing_not_imported') {
    return 'Publicación no importada';
  }
  return typeLabelByType[type] || type || '-';
};

const toDisplayStock = (value) => (value === null || value === undefined ? '-' : Number(value));

const getIssueMercuryStock = (issue) => toDisplayStock(issue?.mercuryStock);
const getIssueChannelStock = (issue, context) =>
  toDisplayStock(context?.channelStock ?? issue?.channelStock);
const bulkProblemLabelByKey = {
  missing_stock_record: 'Falta stock central',
  stock_mismatch: 'Diferencias de stock',
  fanout_failed: 'Error de canal al actualizar',
  listing_not_found: 'Publicación no encontrada',
};

const bulkProblemKeyByType = {
  stock_record_missing: 'missing_stock_record',
  warehouse_not_found: 'missing_stock_record',
  stock_mismatch: 'stock_mismatch',
  fanout_failed: 'fanout_failed',
  listing_not_found: 'listing_not_found',
};
const getBulkProblemKey = (issue) =>
  requiresManualVariationRemap(issue)
    ? 'manual_variation_remap'
    : isStaleVariationMappingIssue(issue)
    ? 'listing_not_found'
    : bulkProblemKeyByType[issue?.type] || issue?.type;

const defaultBulkPreviewColumns = [
  { key: 'sku', label: 'SKU', render: (row) => row.sku || '-' },
  { key: 'listingTitle', label: 'Publicación', render: (row) => row.listingTitle || '-' },
  { key: 'channel', label: 'Canal', render: (row) => getChannelLabel(row.channel) },
];

const bulkModalConfigByProblem = {
  missing_stock_record: {
    problemName: 'Falta stock central',
    title: 'Crear stock central',
    action: 'create_missing_stock_record',
    explanation:
      'Vamos a crear el stock central en MercuryCom usando el stock actual de cada canal como stock inicial.',
    impact:
      'No vamos a modificar el stock en el canal. Solo vamos a crear registros centrales en MercuryCom.',
    confirmLabel: (count) => `Confirmo crear stock central para ${count} publicaciones.`,
    cta: 'Confirmar y crear stock',
    successToast: (count) => ({
      title: 'Stock central creado',
      lines: [`Creamos el registro de stock para ${count} publicaciones.`],
    }),
    previewColumns: [
      { key: 'sku', label: 'SKU', render: (row) => row.sku || '-' },
      { key: 'listingTitle', label: 'Publicación', render: (row) => row.listingTitle || '-' },
      { key: 'channel', label: 'Canal', render: (row) => getChannelLabel(row.channel) },
      {
        key: 'initialStock',
        label: 'Stock inicial',
        render: (row) => `${row.channelStock ?? '-'} unidades`,
      },
    ],
  },
  listing_not_found: {
    problemName: 'Publicación no encontrada',
    title: 'Releer publicaciones',
    action: 'reimport_listing_from_channel',
    explanation:
      'Vamos a releer el canal para verificar si estas publicaciones todavía existen.',
    impact:
      'No vamos a modificar stock. Solo vamos a actualizar la información de las publicaciones.',
    confirmLabel: (count) => `Confirmo releer ${count} publicaciones.`,
    cta: 'Releer publicaciones',
    successToast: (count) => ({
      title: 'Publicaciones releídas',
      lines: [`Releímos ${count} publicaciones correctamente.`],
    }),
    previewColumns: defaultBulkPreviewColumns,
  },
};

const bulkMismatchActionConfigByAction = {
  update_channel_from_mercury: {
    action: 'update_channel_from_mercury',
    title: 'Confirmar actualización',
    explanation:
      'Vas a actualizar los canales seleccionados usando MercuryCom como fuente.',
    impact:
      'Esto modificará el stock en los canales seleccionados.',
    confirmLabel: (count) =>
      `Confirmo actualizar ${count} publicaciones usando MercuryCom como fuente.`,
    cta: 'Confirmar y actualizar canales',
    successToast: (count) => ({
      title: 'Diferencias resueltas',
      lines: [`Actualizamos ${count} publicaciones usando MercuryCom como fuente.`],
    }),
    previewColumns: [
      { key: 'sku', label: 'SKU', render: (row) => row.sku || '-' },
      { key: 'channel', label: 'Canal', render: (row) => getChannelLabel(row.channel) },
      { key: 'mercuryStock', label: 'Stock MercuryCom', render: (row) => `${toDisplayStock(row.mercuryStock)} unidades` },
      { key: 'channelStock', label: 'Stock canal actual', render: (row) => `${toDisplayStock(row.channelStock)} unidades` },
      { key: 'newChannelStock', label: 'Nuevo stock canal', render: (row) => `${toDisplayStock(row.mercuryStock)} unidades` },
    ],
  },
  use_channel_stock_as_mercury: {
    action: 'use_channel_stock_as_mercury',
    title: 'Confirmar cambio de stock central',
    explanation:
      'Vas a usar el stock del canal como nuevo stock central de MercuryCom.',
    impact:
      'Después de actualizar MercuryCom, se sincronizarán las publicaciones asociadas a cada SKU. Usalo solo si el stock del canal es el correcto.',
    confirmLabel: (count) =>
      `Confirmo cambiar el stock central de MercuryCom para ${count} SKUs usando el stock del canal.`,
    cta: 'Confirmar y actualizar MercuryCom',
    successToast: (count) => ({
      title: 'Stock central actualizado',
      lines: [
        `Actualizamos el stock central de ${count} SKUs usando el stock del canal.`,
        'Luego sincronizamos las publicaciones asociadas.',
      ],
    }),
    previewColumns: [
      { key: 'sku', label: 'SKU', render: (row) => row.sku || '-' },
      { key: 'channel', label: 'Canal', render: (row) => getChannelLabel(row.channel) },
      { key: 'mercuryStock', label: 'MercuryCom actual', render: (row) => `${toDisplayStock(row.mercuryStock)} unidades` },
      { key: 'channelStock', label: 'Stock canal', render: (row) => `${toDisplayStock(row.channelStock)} unidades` },
      { key: 'newMercuryStock', label: 'Nuevo MercuryCom', render: (row) => `${toDisplayStock(row.channelStock)} unidades` },
    ],
  },
};

function StockInconsistenciesDashboard() {
  const navigate = useNavigate();
  const { getUser } = useAuth();
  const { refreshSummary } = useStockInconsistencySummary();
  const currentUser = getUser();
  const accessToken = currentUser?.accessToken || '';
  const authUser = useMemo(() => (accessToken ? { accessToken } : null), [accessToken]);

  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState(defaultFilters);
  const [appliedFilters, setAppliedFilters] = useState(defaultFilters);
  const [activeCard, setActiveCard] = useState('open');
  const [activeQuickPreset, setActiveQuickPreset] = useState('all');
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [detecting, setDetecting] = useState(false);
  const [resolvingBulk, setResolvingBulk] = useState(false);
  const [toast, setToast] = useState(null);
  const [selectedIssue, setSelectedIssue] = useState(null);
  const [selectedIssueContext, setSelectedIssueContext] = useState(null);
  const [selectedIssueContextLoading, setSelectedIssueContextLoading] = useState(false);
  const [resolveAction, setResolveAction] = useState('');
  const [resolveConfirmed, setResolveConfirmed] = useState(false);
  const [resolving, setResolving] = useState(false);
  const resolvingRef = useRef(false);
  const resolvingBulkRef = useRef(false);
  const [stockMismatchStep, setStockMismatchStep] = useState('choose');
  const [bulkModalOpen, setBulkModalOpen] = useState(false);
  const [bulkConfirmed, setBulkConfirmed] = useState(false);
  const [bulkAction, setBulkAction] = useState('');

  const pageSize = 20;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const queryKey = useMemo(() => JSON.stringify({ page, filters: appliedFilters }), [page, appliedFilters]);
  const selectedChannelLabel = getChannelLabel(selectedIssue?.channel);
  const selectedIssueUsesStockSourceFlow = usesStockSourceResolutionFlow(selectedIssue);
  const selectedIssueHasLiveChannelStock = hasLiveChannelStock(selectedIssueContext);
  const selectedIssueAvailableActions = useMemo(() => {
    const actions = selectedIssueContext?.availableActions;
    return Array.isArray(actions)
      ? new Set(actions)
      : new Set(['update_channel_from_mercury', 'use_channel_stock_as_mercury']);
  }, [selectedIssueContext]);
  const isSelectedIssueActionAvailable = (action) => selectedIssueAvailableActions.has(action);
  const selectedIssueLiveChannelStock = selectedIssueHasLiveChannelStock
    ? Number(selectedIssueContext.channelStock)
    : null;
  const selectedIssueChannelStockLabel = selectedIssueContextLoading
    ? 'Consultando...'
    : selectedIssueHasLiveChannelStock
      ? selectedIssueLiveChannelStock
      : 'Sin dato live';

  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(null), 4800);
    return () => clearTimeout(t);
  }, [toast]);

  useEffect(() => {
    if (!authUser || !selectedIssue) {
      setSelectedIssueContext(null);
      setSelectedIssueContextLoading(false);
      return undefined;
    }

    let alive = true;
    setSelectedIssueContext(null);
    setSelectedIssueContextLoading(true);

    (async () => {
      try {
        const response = await stockyApi.getStockInconsistencyContext(authUser, selectedIssue.id);
        if (alive) {
          setSelectedIssueContext(response?.data || null);
        }
      } catch {
        if (alive) {
          setSelectedIssueContext(null);
        }
      } finally {
        if (alive) {
          setSelectedIssueContextLoading(false);
        }
      }
    })();

    return () => {
      alive = false;
    };
  }, [authUser, selectedIssue?.id]);

  useEffect(() => {
    if (!authUser) return;
    let alive = true;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const [summaryResponse, listResponse] = await Promise.all([
          stockyApi.getStockInconsistencySummary(authUser, { refresh: 'false' }),
          stockyApi.getStockInconsistencies(authUser, {
            status: appliedFilters.status, type: appliedFilters.type, severity: appliedFilters.severity,
            channel: appliedFilters.channel, sku: appliedFilters.sku, search: appliedFilters.search,
            page, pageSize, refresh: 'false',
          }),
        ]);
        if (!alive) return;
        setSummary(summaryResponse?.data || null);
        setRows(Array.isArray(listResponse?.data?.items) ? listResponse.data.items : []);
        setTotal(Number(listResponse?.data?.total || 0));
        setSelectedIds([]);
      } catch {
        if (!alive) return;
        setError('No pudimos cargar diferencias de stock.');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [authUser, queryKey]);

  const detectAndRefresh = async ({ showToast = true, clearError = true } = {}) => {
    if (!authUser) return;
    setDetecting(true);
    if (clearError) {
      setError('');
    }
    try {
      const detectResponse = await stockyApi.detectStockInconsistencies(authUser, {
        channel: appliedFilters.channel || undefined,
        sku: appliedFilters.sku || undefined,
      });

      const [summaryResponse, listResponse] = await Promise.all([
        stockyApi.getStockInconsistencySummary(authUser, { refresh: 'false' }),
        stockyApi.getStockInconsistencies(authUser, {
          status: appliedFilters.status, type: appliedFilters.type, severity: appliedFilters.severity,
          channel: appliedFilters.channel, sku: appliedFilters.sku, search: appliedFilters.search,
          page, pageSize, refresh: 'false',
        }),
      ]);

      setSummary(summaryResponse?.data || null);
      setRows(Array.isArray(listResponse?.data?.items) ? listResponse.data.items : []);
      setTotal(Number(listResponse?.data?.total || 0));
      if (showToast) {
        const count = Number(detectResponse?.data?.open || summaryResponse?.data?.totalOpen || 0);
        setToast({
          kind: 'positive',
          title: 'Validación ejecutada correctamente',
          lines: [`Encontramos ${count} problemas abiertos.`],
        });
      }
      refreshSummary({ silent: true });
      return true;
    } catch {
      if (clearError) {
        setError('No pudimos ejecutar la detección de diferencias.');
      }
      return false;
    } finally {
      setDetecting(false);
    }
  };

  const runDetectNow = async () => {
    await detectAndRefresh({ showToast: true, clearError: true });
  };

  const openResolve = (row, overrideAction) => {
    setSelectedIssue(row);
    setResolveAction(
      overrideAction ||
        (requiresManualVariationRemap(row)
          ? 'manual_review'
          : isStaleVariationMappingIssue(row)
          ? 'reimport_listing_from_channel'
          : actionByType[row.type]?.key || 'retry_update'),
    );
    setResolveConfirmed(false);
    setStockMismatchStep('choose');
  };

  const closeResolveModal = () => {
    setSelectedIssue(null);
    setResolveAction('');
    setStockMismatchStep('choose');
    setResolveConfirmed(false);
  };

  const resolveCurrentIssue = async () => {
    if (!authUser || !selectedIssue || resolvingRef.current) return;
    if (!resolveAction) {
      const nextToast = buildResolutionErrorToast({
        issue: selectedIssue,
        action: '',
        error: { response: { data: { message: 'No encontramos la acción seleccionada para esta resolución.' } } },
        bulkCount: 1,
      });
      setToast(nextToast);
      setError(nextToast.lines?.[1] || 'No pudimos resolver la inconsistencia seleccionada.');
      return;
    }
    if (
      resolveAction === 'use_channel_stock_as_mercury' &&
      usesStockSourceResolutionFlow(selectedIssue) &&
      !selectedIssueHasLiveChannelStock
    ) {
      const nextToast = buildResolutionErrorToast({
        issue: selectedIssue,
        action: resolveAction,
        error: {
          response: {
            data: {
              message:
                'Todavía no tenemos una lectura live del canal. Esperá a que termine la consulta antes de usar ese stock.',
            },
          },
        },
        bulkCount: 1,
      });
      setToast(nextToast);
      setError(nextToast.lines?.[1] || 'No pudimos validar el stock live del canal.');
      return;
    }
    resolvingRef.current = true;
    setResolving(true);
    setError('');
    try {
      const resolutionResponse = await stockyApi.resolveStockInconsistency(authUser, selectedIssue.id, {
        action: resolveAction,
        confirmed: confirmRequiredActions.has(resolveAction)
          ? (usesStockSourceResolutionFlow(selectedIssue) ? true : resolveConfirmed)
          : true,
      });
      const partialResolution = resolutionResponse?.data?.status === 'partial';
      const autoSuppressed = resolutionResponse?.data?.autoSuppressed === true;
      const skippedRemoteMutation = resolutionResponse?.data?.skippedRemoteMutation === true;
      const skippedFanoutTargets = Array.isArray(resolutionResponse?.data?.skippedFanoutTargets)
        ? resolutionResponse.data.skippedFanoutTargets
        : [];
      const goToInconsistenciesAction = {
        label: 'Volver a Alertas de stock',
        onClick: () => {
          setToast(null);
          navigate('/stock-inconsistencies');
        },
      };
      if (partialResolution) {
        setToast({
          kind: 'warning',
          title: 'Resolución parcial',
          lines: [
            resolutionResponse?.data?.message ||
              'Algunas publicaciones todavía necesitan revisión.',
          ],
          actions: [goToInconsistenciesAction],
        });
      } else if (resolveAction === 'ignore') {
        setToast({
          kind: 'info',
          title: 'Caso ignorado',
          lines: ['Este caso dejó de aparecer en la vista activa y no volverá a recrearse mientras la supresión esté vigente.'],
          actions: [goToInconsistenciesAction],
        });
      } else if (autoSuppressed) {
        // Can happen for any issue type resolved via retry_update /
        // update_channel_from_mercury when the underlying ML listing turns
        // out to be non-mutable — not just stock_mismatch / fanout_failed —
        // so this must not be gated behind
        // usesStockSourceResolutionFlow.
        setToast({
          kind: 'info',
          title: 'Caso ignorado automáticamente',
          lines: [resolutionResponse?.data?.message || 'La publicación no admite actualizaciones de stock. Suprimimos este caso.'],
          actions: [goToInconsistenciesAction],
        });
      } else if (skippedRemoteMutation) {
        setToast({
          kind: 'warning',
          title: 'Publicación no actualizable',
          lines: [resolutionResponse?.data?.message || 'No enviamos una actualización de stock al canal.'],
          actions: [goToInconsistenciesAction],
        });
      } else if (skippedFanoutTargets.length > 0) {
        setToast({
          kind: 'positive',
          title: 'Alerta resuelta',
          lines: [
            resolutionResponse?.data?.message ||
              `Actualizamos las publicaciones disponibles y suprimimos ${skippedFanoutTargets.length} destino(s) que el canal no permite actualizar.`,
          ],
          actions: [goToInconsistenciesAction],
        });
      } else if (usesStockSourceResolutionFlow(selectedIssue)) {
        setToast(
          resolveAction === 'update_channel_from_mercury'
            ? {
                kind: 'positive',
                title: selectedIssue.type === 'stock_mismatch' ? 'Diferencia resuelta' : 'Alerta resuelta',
                lines: [`Actualizamos ${selectedChannelLabel} usando MercuryCom como fuente.`],
                actions: [
                  goToInconsistenciesAction,
                  {
                    label: 'Ver movimiento',
                    secondary: true,
                    onClick: () => {
                      setToast(null);
                      navigate('/movimientos');
                    },
                  },
                ],
              }
            : {
                kind: 'positive',
                title: 'Stock central actualizado',
                lines: [
                  `Actualizamos MercuryCom usando el stock de ${selectedChannelLabel}. Luego sincronizamos las publicaciones asociadas.`,
                ],
                actions: [
                  goToInconsistenciesAction,
                  {
                    label: 'Ver movimiento',
                    secondary: true,
                    onClick: () => {
                      setToast(null);
                      navigate('/movimientos');
                    },
                  },
                ],
              },
        );
      }
      closeResolveModal();
      refreshSummary({ silent: true });
      const ownToastShown =
        partialResolution ||
        resolveAction === 'ignore' ||
        autoSuppressed ||
        skippedRemoteMutation ||
        usesStockSourceResolutionFlow(selectedIssue);
      await detectAndRefresh({
        showToast: !ownToastShown,
        clearError: true,
      });
    } catch (error) {
      if (getStockResolutionErrorCode(error) === ML_STALE_VARIATION_MAPPING_CODE) {
        const recoveryMode = String(error?.response?.data?.recoveryMode || 'automatic_reimport');
        const remoteVariationIds = Array.isArray(error?.response?.data?.remoteVariationIds)
          ? error.response.data.remoteVariationIds
          : [];
        const requiresManualRemap = recoveryMode === 'manual_remap_required';
        const staleIssue = {
          ...selectedIssue,
          rawErrorCode: ML_STALE_VARIATION_MAPPING_CODE,
          metadata: {
            ...(selectedIssue.metadata || {}),
            resolutionBlockerCode: ML_STALE_VARIATION_MAPPING_CODE,
            recommendedAction: requiresManualRemap ? null : 'reimport_listing_from_channel',
            staleVariationRecoveryMode: recoveryMode,
            staleVariationMapping: {
              ...(selectedIssue.metadata?.staleVariationMapping || {}),
              remoteVariationIds,
            },
          },
        };
        closeResolveModal();
        await detectAndRefresh({ showToast: false, clearError: false });
        setToast({
          kind: 'warning',
          title: requiresManualRemap
            ? 'La variación necesita revisión manual'
            : 'La variación cambió en Mercado Libre',
          lines: requiresManualRemap
            ? [
                'No modificamos stock ni ejecutamos fan-out.',
                'Mercado Libre devuelve otras variaciones y MercuryCom no puede elegir una automáticamente.',
              ]
            : [
                'No modificamos stock ni ejecutamos fan-out.',
                'La referencia guardada ya no existe. Releé la publicación para actualizar el vínculo.',
              ],
          actions: [
            {
              label: requiresManualRemap ? 'Ver pasos' : 'Releer ahora',
              onClick: () => {
                setToast(null);
                openResolve(
                  staleIssue,
                  requiresManualRemap ? 'manual_review' : 'reimport_listing_from_channel',
                );
              },
            },
          ],
        });
        return;
      }
      const nextToast = buildResolutionErrorToast({
        issue: selectedIssue,
        action: resolveAction,
        error,
        bulkCount: 1,
      });
      setToast(nextToast);
      setError(nextToast.lines?.[1] || 'No pudimos resolver la inconsistencia seleccionada.');
    } finally {
      resolvingRef.current = false;
      setResolving(false);
    }
  };

  const applyCardFilter = (key) => {
    setActiveCard(key);
    const next = { ...filters, status: ['open', 'in_progress'] };
    if (key === 'critical') next.severity = ['critical']; else next.severity = [];
    if (key === 'mismatch') next.type = ['stock_mismatch'];
    else if (key === 'missing') next.type = ['publication_missing'];
    else next.type = [];
    setFilters(next);
    setAppliedFilters({ ...next, type: expandTypeFilters(next.type) });
    setPage(1);
  };

  const applyQuickPreset = (preset) => {
    const quickKey = preset?.type?.includes('stock_mismatch')
      ? 'mismatch'
      : preset?.type?.includes('listing_not_found')
        ? 'listing_not_found'
        : preset?.type?.includes('insufficient_stock')
          ? 'insufficient_stock'
          : preset?.type?.includes('fanout_failed')
            ? 'fanout_failed'
            : preset?.type?.includes('channel_sync_failed')
              ? 'channel_sync_failed'
              : preset?.type?.includes('unknown_stock_error')
                ? 'unknown_stock_error'
                : preset?.channel === 'mercado_libre'
                  ? 'mercado_libre'
                  : preset?.channel === 'tienda_nube'
                    ? 'tienda_nube'
                    : preset?.severity?.includes('critical')
                      ? 'critical'
                      : preset?.type?.includes('stock_record_missing') || preset?.type?.includes('warehouse_not_found')
                        ? 'publication_missing'
                        : 'all';
    const next = {
      ...defaultFilters,
      ...preset,
      status: ['open', 'in_progress'],
    };
    setActiveQuickPreset(quickKey);
    setFilters(next);
    setAppliedFilters({ ...next, type: expandTypeFilters(next.type) });
    setPage(1);
  };

  const quickChip = (key, label, preset) => (
    <Button
      size="tiny"
      basic={activeQuickPreset !== key}
      color={activeQuickPreset === key ? 'violet' : undefined}
      onClick={() => {
      applyQuickPreset(preset);
    }}
    >
      {label}
    </Button>
  );

  const quickTypeChips = [
    ['Publicación sin registrar', { type: ['publication_missing'] }],
    ['Publicación no encontrada', { type: ['listing_not_found'] }],
    ['Diferencias de stock', { type: ['stock_mismatch'] }],
    ['Stock insuficiente', { type: ['insufficient_stock'] }],
    ['Error de canal al actualizar', { type: ['fanout_failed'] }],
    ['Sync de canal fallido', { type: ['channel_sync_failed'] }],
    ['Error desconocido', { type: ['unknown_stock_error'] }],
  ];

  const selectedRows = rows.filter((r) => selectedIds.includes(r.id));
  const selectedBulkProblemKeys = Array.from(
    new Set(selectedRows.map((row) => getBulkProblemKey(row))),
  );
  const hasMixedBulkSelection = selectedBulkProblemKeys.length > 1;
  const selectedBulkProblemKey = selectedBulkProblemKeys.length === 1 ? selectedBulkProblemKeys[0] : '';
  const selectedBulkConfig = selectedBulkProblemKey
    ? bulkModalConfigByProblem[selectedBulkProblemKey] || null
    : null;
  const isBulkStockSourceFlow =
    selectedBulkProblemKey === 'stock_mismatch' || selectedBulkProblemKey === 'fanout_failed';
  const hasBulkResolutionFlow = isBulkStockSourceFlow || Boolean(selectedBulkConfig);
  const groupedChannelStocksBySku = selectedRows.reduce((acc, row) => {
    const skuKey = String(row.sku || row.itemId || row.id || '').trim();
    const channelStock = toDisplayStock(row.channelStock);
    if (!skuKey || channelStock === '-') return acc;
    const bucket = acc[skuKey] || new Set();
    bucket.add(String(channelStock));
    acc[skuKey] = bucket;
    return acc;
  }, {});
  const hasConflictingChannelStocksForBulkMismatch =
    isBulkStockSourceFlow &&
    Object.values(groupedChannelStocksBySku).some((stockSet) => stockSet.size > 1);
  const selectedBulkActionConfig = isBulkStockSourceFlow
    ? bulkMismatchActionConfigByAction[bulkAction] || null
    : selectedBulkConfig;
  const bulkActionDisabledReason =
    selectedIds.length === 0
      ? ''
      : hasMixedBulkSelection
        ? 'Para usar acción masiva, seleccioná alertas del mismo problema.'
        : !hasBulkResolutionFlow
          ? 'La acción masiva todavía no está disponible para este problema.'
          : '';
  const canOpenBulkModal = selectedIds.length > 0 && !hasMixedBulkSelection && hasBulkResolutionFlow;
  const selectedTypeSummary = Object.entries(
    selectedRows.reduce((acc, row) => {
      const problemKey = getBulkProblemKey(row);
      const problemName = bulkProblemLabelByKey[problemKey] || getTypeLabel(row.type, row.channel);
      acc[problemName] = (acc[problemName] || 0) + 1;
      return acc;
    }, {}),
  )
    .map(([label, count]) => `${count} ${label}`)
    .join(' · ');
  const bulkPreviewRows = selectedRows.slice(0, 5);
  const bulkRemainingRows = Math.max(selectedRows.length - bulkPreviewRows.length, 0);
  const bulkStockSourceFlowTitle =
    selectedBulkProblemKey === 'fanout_failed'
      ? 'Resolver errores de canal al actualizar'
      : 'Resolver diferencias de stock';
  const bulkStockSourceSelectionLabel =
    selectedBulkProblemKey === 'fanout_failed'
      ? 'errores de canal al actualizar'
      : 'diferencias de stock';
  const bulkStockSourceExplanation =
    selectedBulkProblemKey === 'fanout_failed'
      ? `No pudimos actualizar ${selectedRows.length === 1 ? 'el canal' : 'los canales'} con el stock de MercuryCom. Elegí qué stock querés conservar para toda la selección.`
      : 'MercuryCom y el canal tienen valores distintos. Elegí qué stock querés conservar para toda la selección.';

  const toggleRowSelection = (row, checked) => {
    if (!checked) {
      setSelectedIds((prev) => prev.filter((id) => id !== row.id));
      return;
    }

    setSelectedIds((prev) => (prev.includes(row.id) ? prev : [...prev, row.id]));
  };

  const selectVisibleRows = () => {
    if (rows.length === 0) return;
    setSelectedIds(rows.map((row) => row.id));
  };

  const runBulkAction = async () => {
    if (
      !authUser ||
      !selectedBulkActionConfig ||
      selectedRows.length === 0 ||
      resolvingBulkRef.current
    ) return;
    if (!selectedBulkActionConfig.action) {
      const nextToast = buildResolutionErrorToast({
        issue: selectedRows[0],
        action: '',
        error: { response: { data: { message: 'No encontramos la acción seleccionada para esta resolución masiva.' } } },
        bulkCount: selectedRows.length,
      });
      setToast(nextToast);
      setError(nextToast.lines?.[1] || 'No pudimos ejecutar la acción masiva.');
      return;
    }
    resolvingBulkRef.current = true;
    setResolvingBulk(true);
    let shouldRevalidateAfterBulk = false;
    const resolutionResults = [];
    try {
      shouldRevalidateAfterBulk = true;
      for (const row of selectedRows) {
        const response = await stockyApi.resolveStockInconsistency(authUser, row.id, {
          action: selectedBulkActionConfig.action,
          confirmed: true,
          skipRevalidation: true,
        });
        resolutionResults.push(response?.data || {});
      }
      const resolutionSummary = summarizeBulkResolutionResponses(resolutionResults);
      const hasIncompleteMarketplaceWork =
        resolutionSummary.partial > 0 || resolutionSummary.primarySkipped > 0;
      const successToast = hasIncompleteMarketplaceWork
        ? {
            title: 'Resolución masiva parcial',
            lines: [
              `${resolutionSummary.resolved} de ${resolutionSummary.processed} alertas quedaron completamente resueltas.`,
              resolutionSummary.primarySkipped > 0
                ? `${resolutionSummary.primarySkipped} publicación(es) principales no admitieron una actualización remota.`
                : null,
              resolutionSummary.fanoutSkipped > 0
                ? `${resolutionSummary.fanoutSkipped} destino(s) no actualizables fueron omitidos y sus alertas quedaron suprimidas.`
                : null,
              resolutionSummary.partial > 0
                ? `${resolutionSummary.partial} resolución(es) requieren seguimiento.`
                : null,
            ].filter(Boolean),
          }
        : resolutionSummary.autoSuppressed > 0 || resolutionSummary.fanoutSkipped > 0
          ? {
              title: 'Resolución masiva completada',
              lines: [
                `${resolutionSummary.resolved} de ${resolutionSummary.processed} alertas quedaron resueltas.`,
                resolutionSummary.autoSuppressed > 0
                  ? `${resolutionSummary.autoSuppressed} publicación(es) principales no actualizables quedaron suprimidas.`
                  : null,
                resolutionSummary.fanoutSkipped > 0
                  ? `${resolutionSummary.fanoutSkipped} destino(s) no actualizables fueron omitidos y sus alertas quedaron suprimidas.`
                  : null,
              ].filter(Boolean),
            }
          : selectedBulkActionConfig.successToast(selectedRows.length);
      setToast({
        kind: hasIncompleteMarketplaceWork ? 'warning' : 'positive',
        ...successToast,
        actions: [
          {
            label: 'Ver movimientos',
            onClick: () => {
              setToast(null);
              navigate('/movimientos');
            },
          },
          {
            label: 'Volver a Alertas de stock',
            secondary: true,
            onClick: () => {
              setToast(null);
              navigate('/stock-inconsistencies');
            },
          },
        ],
      });
      setSelectedIds([]);
      setBulkModalOpen(false);
      setBulkConfirmed(false);
      setBulkAction('');
      refreshSummary({ silent: true });
    } catch (error) {
      const nextToast = buildResolutionErrorToast({
        issue: selectedRows[0],
        action: selectedBulkActionConfig?.action,
        error,
        bulkCount: selectedRows.length,
      });
      setToast(nextToast);
      setError(nextToast.lines?.[1] || 'No pudimos ejecutar la acción masiva.');
    } finally {
      if (shouldRevalidateAfterBulk) {
        await detectAndRefresh({ showToast: false, clearError: false });
      }
      resolvingBulkRef.current = false;
      setResolvingBulk(false);
    }
  };

  useEffect(() => {
    if (!bulkModalOpen) return;
    if (canOpenBulkModal) return;
    setBulkModalOpen(false);
    setBulkConfirmed(false);
    setBulkAction('');
  }, [bulkModalOpen, canOpenBulkModal]);

  const criticalRows = rows.filter((r) => r.severity === 'critical');
  const mismatchCritical = criticalRows.filter((r) => r.type === 'stock_mismatch');
  const missingCritical = criticalRows.filter((r) => r.type === 'stock_record_missing' || r.type === 'warehouse_not_found');
  const priorityKey = mismatchCritical.length > 0 ? 'mismatch' : missingCritical.length > 0 ? 'missing' : 'critical';
  const priorityMsg = criticalRows.length
    ? priorityKey === 'mismatch'
      ? `${criticalRows.length} casos críticos requieren atención. La mayoría son diferencias de stock${mismatchCritical.length ? ` (${mismatchCritical.length})` : ''}.`
      : priorityKey === 'missing'
        ? `${criticalRows.length} casos críticos requieren atención. La mayoría son publicaciones sin registrar${missingCritical.length ? ` (${missingCritical.length})` : ''}.`
        : `${criticalRows.length} casos críticos requieren atención.`
    : 'No hay casos críticos en este momento.';
  const priorityButtonLabel =
    priorityKey === 'mismatch' ? 'Resolver diferencias de stock' : 'Resolver alertas críticas';

  if (!authUser) return <Container style={pageShellStyle}><Message warning content="Necesitas iniciar sesión para ver diferencias." /></Container>;
  if (loading) return <div className="centered-loader-container"><Dimmer active inverted><Loader inverted size="huge">Cargando diferencias...</Loader></Dimmer></div>;

  return (
    <Container fluid style={pageShellStyle}>
      {toast ? (
        <div style={{ position: 'fixed', right: 20, top: 80, zIndex: 1000 }}>
          <Message
            positive={toast.kind === 'positive'}
            negative={toast.kind === 'negative'}
            warning={toast.kind === 'warning'}
            onDismiss={() => setToast(null)}
          >
            <Message.Header>{toast.title || 'Listo'}</Message.Header>
            {(toast.lines || []).map((line, index) => (
              <p key={`toast-line-${index}`}>{line}</p>
            ))}
            {Array.isArray(toast.actions) && toast.actions.length > 0 ? (
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
                {toast.actions.map((action) => (
                  <Button
                    key={action.label}
                    size="tiny"
                    basic={action.secondary === true}
                    color={action.secondary === true ? undefined : 'violet'}
                    onClick={action.onClick}
                  >
                    {action.label}
                  </Button>
                ))}
              </div>
            ) : null}
          </Message>
        </div>
      ) : null}

      <Segment style={pageFrameStyle}>
        <div style={pageBodyStyle}>
          <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <Button color="violet" loading={detecting} onClick={runDetectNow}>Detectar diferencias ahora</Button>
          </div>

          {error ? <Message negative style={{ marginTop: 14 }}><Message.Header>Error</Message.Header><p>{error}</p></Message> : null}

          <div style={{ ...sectionTitleStyle, marginTop: 18 }}>Resumen</div>
          <Grid stackable columns={5}>
            {[
              { key: 'open', label: 'Problemas abiertos', value: Number(summary?.totalOpen || 0) },
              { key: 'critical', label: 'Críticos', value: Number(summary?.critical || 0) },
              { key: 'mismatch', label: 'Diferencias de stock', value: Number(summary?.stockMismatches || 0) },
              { key: 'missing', label: 'Publicación sin registrar', value: Number(summary?.stockRecordMissing || 0) },
              { key: 'validation', label: 'Última validación', value: summary?.lastValidationAt ? formatRelative(summary.lastValidationAt) : '-' },
            ].map((card) => (
              <Grid.Column key={card.key}>
                <Segment
                  role="button"
                  onClick={() => card.key !== 'validation' && applyCardFilter(card.key)}
                  style={{ ...surfaceCardStyle, padding: 16, border: activeCard === card.key ? '1px solid #7C3AED' : '1px solid #E5E7EB', cursor: card.key !== 'validation' ? 'pointer' : 'default' }}
                >
                  <div style={{ color: '#6B7280' }}>{card.label}</div>
                  <div style={{ fontSize: 24, fontWeight: 800 }}>{card.value}</div>
                </Segment>
              </Grid.Column>
            ))}
          </Grid>

          <Segment style={{ ...surfaceCardStyle, marginTop: 14, border: '1px solid #E9D5FF', background: '#FAF5FF' }}>
            <div style={{ fontWeight: 800, marginBottom: 6 }}>Qué resolver primero</div>
            <div style={{ color: '#6D28D9', marginBottom: 8 }}>{priorityMsg}</div>
            <Button
              size="small"
              color="violet"
              disabled={!criticalRows.length}
              onClick={() =>
                applyQuickPreset(
                  priorityKey === 'mismatch'
                    ? { type: ['stock_mismatch'] }
                    : { severity: ['critical'], type: [], channel: '', sku: '', search: '' },
                )
              }
            >
              {priorityButtonLabel}
            </Button>
          </Segment>

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
            {quickChip('all', 'Todos', { type: [], severity: [], channel: '', sku: '', search: '' })}
            {quickChip('critical', 'Críticos', { severity: ['critical'], type: [], channel: '', sku: '', search: '' })}
            {quickTypeChips.map(([label, preset]) => quickChip(
              preset.channel === 'mercado_libre'
                ? 'mercado_libre'
                : preset.channel === 'tienda_nube'
                  ? 'tienda_nube'
                  : preset.type?.[0] || label,
              label,
              { ...preset, severity: [], channel: '', sku: '', search: '' },
            ))}
            {quickChip('mercado_libre', 'Mercado Libre', { channel: 'mercado_libre', type: [], severity: [], sku: '', search: '' })}
            {quickChip('tienda_nube', 'Tienda Nube', { channel: 'tienda_nube', type: [], severity: [], sku: '', search: '' })}
            <Button size="tiny" basic onClick={() => setAdvancedOpen((v) => !v)}>{advancedOpen ? 'Ocultar filtros' : 'Más filtros'}</Button>
          </div>

          {advancedOpen ? (
            <Segment style={{ ...surfaceCardStyle, padding: 16, marginTop: 10 }}>
              <Form onSubmit={(e) => { e.preventDefault(); setAppliedFilters({ ...filters, sku: (filters.sku || '').trim(), search: (filters.search || '').trim() }); setPage(1); }}>
                <Grid stackable columns={3}>
                  <Grid.Row>
                    <Grid.Column><Form.Field><label>Tipo</label><Dropdown multiple search selection options={typeFilterOptions} value={compressTypeFilters(filters.type)} onChange={(_, d) => setFilters((p) => ({ ...p, type: expandTypeFilters(Array.isArray(d.value) ? d.value : []) }))} /></Form.Field></Grid.Column>
                    <Grid.Column><Form.Field><label>Severidad</label><Dropdown multiple selection options={severityOptions} value={filters.severity} onChange={(_, d) => setFilters((p) => ({ ...p, severity: d.value || [] }))} /></Form.Field></Grid.Column>
                    <Grid.Column><Form.Field><label>Estado</label><Dropdown multiple selection options={statusOptions} value={filters.status} onChange={(_, d) => setFilters((p) => ({ ...p, status: d.value || [] }))} /></Form.Field></Grid.Column>
                  </Grid.Row>
                  <Grid.Row>
                    <Grid.Column><Form.Field><label>Canal</label><Dropdown selection options={channelOptions} value={filters.channel} onChange={(_, d) => setFilters((p) => ({ ...p, channel: String(d.value || '') }))} /></Form.Field></Grid.Column>
                    <Grid.Column><Form.Input label="SKU" value={filters.sku} onChange={(e) => setFilters((p) => ({ ...p, sku: e.target.value }))} /></Grid.Column>
                    <Grid.Column><Form.Input label="Buscar" value={filters.search} onChange={(e) => setFilters((p) => ({ ...p, search: e.target.value }))} /></Grid.Column>
                  </Grid.Row>
                </Grid>
                <Button color="blue" type="submit">Aplicar filtros</Button>
              </Form>
            </Segment>
          ) : null}

          <div style={{ ...sectionTitleStyle, marginTop: 20 }}>Casos abiertos</div>
          <Segment style={{ ...surfaceCardStyle, padding: 16 }}>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
              <Button size="tiny" basic onClick={selectVisibleRows}>Seleccionar visibles</Button>
              <Button size="tiny" basic onClick={() => setSelectedIds([])}>Limpiar selección</Button>
              <Popup
                content={bulkActionDisabledReason}
                disabled={!bulkActionDisabledReason}
                position="top center"
                trigger={(
                  <span>
                    <Button
                      size="tiny"
                      color="violet"
                      disabled={!canOpenBulkModal}
                      onClick={() => {
                        setBulkConfirmed(false);
                        setBulkModalOpen(true);
                      }}
                    >
                      Resolver seleccionadas
                    </Button>
                  </span>
                )}
              />
            </div>
            {selectedIds.length > 0 && bulkActionDisabledReason ? (
              <Message warning style={{ marginTop: 0, marginBottom: 12 }}>
                <Message.Header>{bulkActionDisabledReason}</Message.Header>
                {selectedTypeSummary ? <p>{selectedTypeSummary}</p> : null}
              </Message>
            ) : null}

            <div className="responsive-table-container">
              <Table celled striped compact="very" style={{ minWidth: 1050 }}>
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell style={{ width: 40 }} />
                    <Table.HeaderCell>Problema</Table.HeaderCell>
                    <Table.HeaderCell>SKU / Publicación</Table.HeaderCell>
                    <Table.HeaderCell>Canal</Table.HeaderCell>
                    <Table.HeaderCell>Stock</Table.HeaderCell>
                    <Table.HeaderCell>Prioridad</Table.HeaderCell>
                    <Table.HeaderCell>Detectado</Table.HeaderCell>
                    <Table.HeaderCell>Acción</Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {rows.length === 0 ? (
                    <Table.Row><Table.Cell colSpan={8} textAlign="center">No hay diferencias para los filtros seleccionados.</Table.Cell></Table.Row>
                  ) : rows.map((row) => {
                    const action = requiresManualVariationRemap(row)
                      ? { key: 'manual_review', label: 'Ver pasos' }
                      : isStaleVariationMappingIssue(row)
                      ? { key: 'reimport_listing_from_channel', label: 'Releer' }
                      : actionByType[row.type] || { key: 'retry_update', label: 'Resolver' };
                    return (
                      <Table.Row key={row.id}>
                        <Table.Cell collapsing><Checkbox checked={selectedIds.includes(row.id)} onChange={(_, d) => toggleRowSelection(row, Boolean(d.checked))} /></Table.Cell>
                        <Table.Cell>{getTypeLabel(row.type, row.channel, row.metadata)}</Table.Cell>
                        <Table.Cell><div><strong>{row.sku || '-'}</strong></div><div style={{ color: '#6B7280' }}>{row.listingTitle || '-'}</div></Table.Cell>
                        <Table.Cell>{getChannelLabel(row.channel)}</Table.Cell>
                        <Table.Cell>
                          {row.type === 'sale_without_stock'
                            ? `Pedido ${row.metadata?.externalOrderId || '-'} · ${row.metadata?.requestedQuantity ?? '-'} u.`
                            : row.type === 'stock_record_missing' || row.type === 'warehouse_not_found'
                            ? 'Sin registrar'
                            : `${row.mercuryStock ?? '-'} en MercuryCom / ${row.channelStock ?? '-'} en ${getChannelLabel(row.channel)}`}
                        </Table.Cell>
                        <Table.Cell><Label color={getSeverityColor(row.severity)}>{getSeverityLabel(row.severity)}</Label></Table.Cell>
                        <Table.Cell>{formatRelative(row.lastDetectedAt)}</Table.Cell>
                        <Table.Cell>
                          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                            <Button size="mini" color="violet" onClick={() => openResolve(row)}>{action.label}</Button>
                            <Button size="mini" basic color="grey" onClick={() => openResolve(row, 'ignore')}>Ignorar</Button>
                          </div>
                        </Table.Cell>
                      </Table.Row>
                    );
                  })}
                </Table.Body>
              </Table>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 12 }}>
              <Button disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Anterior</Button>
              <div>Página {page} de {totalPages} ({total} registros)</div>
              <Button disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Siguiente</Button>
            </div>
          </Segment>
        </div>
      </Segment>

      <Modal
        open={Boolean(selectedIssue)}
        onClose={() => setSelectedIssue(null)}
        size="small"
        closeOnDimmerClick={false}
        closeOnEscape={false}
        scrolling
      >
        <Modal.Header>{getTypeLabel(selectedIssue?.type, selectedIssue?.channel, selectedIssue?.metadata) || 'Inconsistencia'}</Modal.Header>
        <Modal.Content style={{ maxHeight: 'calc(90vh - 160px)', overflowY: 'auto' }}>
          {selectedIssue ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ display: 'flex', gap: 8 }}>
                <Label color={getSeverityColor(selectedIssue.severity)}>{getSeverityLabel(selectedIssue.severity)}</Label>
                <Label>{getStatusLabel(selectedIssue.status)}</Label>
              </div>
              {selectedIssue.type === 'sale_without_stock' ? (
                <Message warning>
                  <Message.Header>La venta se confirmó en el canal</Message.Header>
                  <p>MercuryCom no tenía unidades suficientes para descontarla. La venta sigue existiendo en el canal; revisar esta alerta no modifica stock.</p>
                  <List>
                    <List.Item><strong>ID de orden:</strong> {selectedIssue.metadata?.externalOrderId || '-'}</List.Item>
                    <List.Item><strong>Fecha de la venta:</strong> {formatDateTime(selectedIssue.metadata?.signalAt || selectedIssue.firstDetectedAt)}</List.Item>
                    <List.Item><strong>Unidades solicitadas:</strong> {selectedIssue.metadata?.requestedQuantity ?? '-'}</List.Item>
                    <List.Item><strong>Unidades disponibles:</strong> {selectedIssue.metadata?.availableQuantity ?? '-'}</List.Item>
                    <List.Item><strong>Estado actual:</strong> {selectedIssue.metadata?.orderStatus || 'Venta confirmada en el canal'}</List.Item>
                  </List>
                </Message>
              ) : null}
              {selectedIssueUsesStockSourceFlow ? (
                <>
                  {stockMismatchStep === 'choose' ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                      <div>
                        <div style={{ fontWeight: 800, fontSize: 18, marginBottom: 4 }}>SKU {selectedIssue.sku || '-'}</div>
                        <div style={{ color: '#111827', marginBottom: 4 }}>{selectedIssue.listingTitle || '-'}</div>
                        <div style={{ color: '#6B7280' }}>{selectedChannelLabel}</div>
                      </div>

                      <div style={{ color: '#4B5563' }}>
                        {selectedIssue.type === 'fanout_failed'
                          ? `No pudimos actualizar ${selectedChannelLabel} con el stock de MercuryCom. Elegí cómo querés resolverlo.`
                          : selectedIssue.type === 'insufficient_stock'
                            ? 'Una venta no pudo descontarse porque MercuryCom no tenía stock suficiente. Elegí qué stock actual querés conservar; la venta no se descontará otra vez.'
                            : `El stock de MercuryCom y ${selectedChannelLabel} no coincide. Elegí cuál querés conservar.`}
                      </div>

                      <Segment style={{ ...surfaceCardStyle, padding: 16 }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: 12, alignItems: 'center', textAlign: 'center' }}>
                          <div>
                            <div style={{ color: '#6B7280', fontWeight: 700, marginBottom: 6 }}>MercuryCom</div>
                            <div style={{ fontSize: 28, fontWeight: 800 }}>{getIssueMercuryStock(selectedIssue)}</div>
                            <div style={{ color: '#6B7280' }}>unidades</div>
                          </div>
                          <div style={{ fontSize: 28, fontWeight: 800, color: '#7C3AED' }}>
                            {selectedIssue.type === 'insufficient_stock' ? '↔' : '≠'}
                          </div>
                          <div>
                            <div style={{ color: '#6B7280', fontWeight: 700, marginBottom: 6 }}>{selectedChannelLabel}</div>
                            <div style={{ fontSize: 28, fontWeight: 800 }}>{selectedIssueChannelStockLabel}</div>
                            {selectedIssueHasLiveChannelStock ? (
                              <div style={{ color: '#6B7280' }}>unidades · lectura live</div>
                            ) : null}
                          </div>
                        </div>
                      </Segment>

                      <div style={{ display: 'grid', gap: 12 }}>
                        {isSelectedIssueActionAvailable('update_channel_from_mercury') ? (
                        <Segment
                          style={{
                            ...surfaceCardStyle,
                            padding: 16,
                            border: '1px solid #DDD6FE',
                            minHeight: 124,
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                            <div style={{ fontSize: 18, fontWeight: 800 }}>Usar stock de MercuryCom</div>
                            <Label color="violet" size="tiny">Recomendado</Label>
                          </div>
                          <div style={{ color: '#4B5563', marginBottom: 14 }}>
                            {selectedIssue.type === 'fanout_failed'
                              ? `${selectedChannelLabel} se actualizará de ${getIssueChannelStock(selectedIssue, selectedIssueContext)} a ${getIssueMercuryStock(selectedIssue)} unidades. Después vamos a sincronizar las publicaciones asociadas.`
                              : `${selectedChannelLabel} pasará de ${getIssueChannelStock(selectedIssue, selectedIssueContext)} a ${getIssueMercuryStock(selectedIssue)} unidades.`}
                          </div>
                          <Button
                            color="violet"
                            size="small"
                            type="button"
                            onClick={() => {
                              setResolveAction('update_channel_from_mercury');
                              setResolveConfirmed(false);
                              setStockMismatchStep('confirm');
                            }}
                          >
                            Usar MercuryCom
                          </Button>
                        </Segment>
                        ) : null}

                        {isSelectedIssueActionAvailable('use_channel_stock_as_mercury') ? (
                        <Segment
                          style={{
                            ...surfaceCardStyle,
                            padding: 16,
                            minHeight: 124,
                          }}
                        >
                          <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 8 }}>Usar stock de {selectedChannelLabel}</div>
                          <div style={{ color: '#4B5563', marginBottom: 14 }}>
                            {selectedIssueContextLoading
                              ? `Estamos consultando el stock actual de ${selectedChannelLabel}.`
                              : selectedIssueHasLiveChannelStock
                                ? selectedIssue.type === 'fanout_failed'
                                  ? `MercuryCom pasará de ${getIssueMercuryStock(selectedIssue)} a ${selectedIssueLiveChannelStock} unidades. Después vamos a sincronizar las publicaciones asociadas.`
                                  : `MercuryCom pasará de ${getIssueMercuryStock(selectedIssue)} a ${selectedIssueLiveChannelStock} unidades.`
                                : `No pudimos obtener una lectura live de ${selectedChannelLabel}. Esta opción seguirá bloqueada para no aplicar un stock desconocido.`}
                          </div>
                          <Button
                            color="violet"
                            basic
                            size="small"
                            type="button"
                            disabled={selectedIssueContextLoading || !selectedIssueHasLiveChannelStock}
                            onClick={() => {
                              setResolveAction('use_channel_stock_as_mercury');
                              setResolveConfirmed(false);
                              setStockMismatchStep('confirm');
                            }}
                          >
                            Usar {selectedChannelLabel}
                          </Button>
                        </Segment>
                        ) : null}
                      </div>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                      <div>
                        <div style={{ fontWeight: 800, fontSize: 18, marginBottom: 4 }}>
                          {resolveAction === 'update_channel_from_mercury'
                            ? 'Confirmar actualización'
                            : 'Confirmar cambio de stock central'}
                        </div>
                        <div style={{ color: '#4B5563' }}>
                          {resolveAction === 'update_channel_from_mercury'
                            ? (selectedIssue.type === 'fanout_failed'
                              ? `Vas a actualizar ${selectedChannelLabel} usando MercuryCom como fuente y luego vamos a reintentar la sincronización del SKU.`
                              : `Vas a usar MercuryCom como fuente.`)
                            : `Vas a usar el stock de ${selectedChannelLabel} como nuevo stock central de MercuryCom.`}
                        </div>
                      </div>

                      <Segment style={{ ...surfaceCardStyle, padding: 16 }}>
                        {resolveAction === 'update_channel_from_mercury' ? (
                          <div style={{ display: 'grid', gap: 12 }}>
                            <div>
                              <div style={{ color: '#6B7280', fontWeight: 700 }}>{selectedChannelLabel}</div>
                              <div style={{ fontSize: 20, fontWeight: 800 }}>
                                {getIssueChannelStock(selectedIssue, selectedIssueContext)} → {getIssueMercuryStock(selectedIssue)} unidades
                              </div>
                            </div>
                            <div>
                              <div style={{ color: '#6B7280', fontWeight: 700 }}>MercuryCom</div>
                              <div style={{ fontSize: 20, fontWeight: 800 }}>
                                Se mantiene en {getIssueMercuryStock(selectedIssue)} unidades
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div style={{ display: 'grid', gap: 12 }}>
                            <div>
                              <div style={{ color: '#6B7280', fontWeight: 700 }}>MercuryCom</div>
                              <div style={{ fontSize: 20, fontWeight: 800 }}>
                                {getIssueMercuryStock(selectedIssue)} → {getIssueChannelStock(selectedIssue, selectedIssueContext)} unidades
                              </div>
                            </div>
                            <div>
                              <div style={{ color: '#6B7280', fontWeight: 700 }}>{selectedChannelLabel}</div>
                              <div style={{ fontSize: 20, fontWeight: 800 }}>
                                Se mantiene en {getIssueChannelStock(selectedIssue, selectedIssueContext)} unidades
                              </div>
                            </div>
                          </div>
                        )}
                      </Segment>

                      {selectedIssue.type === 'insufficient_stock' ? (
                        <Message info style={{ margin: 0 }}>
                          Esta acción reconcilia el stock actual y cierra la alerta histórica. No vuelve a aplicar ni a descontar la venta que originó el caso.
                        </Message>
                      ) : null}

                      {resolveAction === 'update_channel_from_mercury' && selectedIssue.type === 'fanout_failed' ? (
                        <Message info style={{ margin: 0 }}>
                          Después de actualizar {selectedChannelLabel}, vamos a volver a sincronizar las publicaciones asociadas a este SKU.
                        </Message>
                      ) : null}

                      {resolveAction === 'use_channel_stock_as_mercury' ? (
                        <Message warning style={{ margin: 0 }}>
                          Después de actualizar MercuryCom, se sincronizarán las publicaciones asociadas a este SKU.
                        </Message>
                      ) : null}
                    </div>
                  )}
                </>
              ) : null}
              {!selectedIssueUsesStockSourceFlow && (selectedIssueContextLoading || selectedIssueContext?.channelStock !== undefined) ? (
                <div>
                  <strong>Stock de {selectedChannelLabel}:</strong>{' '}
                  {selectedIssueContextLoading
                    ? 'Consultando ahora...'
                    : selectedIssueContext?.channelStock !== null && selectedIssueContext?.channelStock !== undefined
                      ? `${selectedIssueContext.channelStock} unidades${selectedIssueContext?.channelStockSource === 'snapshot' ? ' (snapshot)' : ''}`
                      : 'Sin dato'}
                </div>
              ) : null}

              {resolveAction === 'create_missing_stock_record' ? (
                <Message warning>
                  <Message.Header>Acción recomendada</Message.Header>
                  <p>Vamos a crear el stock central para <strong>{selectedIssue.sku || '-'}</strong> usando el stock de {selectedChannelLabel}: <strong>{selectedIssueContext?.channelStock ?? selectedIssue.channelStock ?? 'Sin dato'}</strong>{(selectedIssueContext?.channelStock ?? selectedIssue.channelStock) !== null && (selectedIssueContext?.channelStock ?? selectedIssue.channelStock) !== undefined ? ' unidades' : ''}.</p>
                  <p>No vamos a modificar el stock en {selectedChannelLabel}. Solo vamos a crear el registro central en MercuryCom.</p>
                </Message>
              ) : null}

              {resolveAction === 'reimport_listing_from_channel' ? (
                <Message info>
                  <Message.Header>Acción recomendada</Message.Header>
                  <p>Vamos a volver a leer la publicación desde {selectedChannelLabel}.</p>
                  <p>Si el SKU ya existe, lo vamos a asociar. Si no existe, vamos a crear el SKU y su stock central en MercuryCom.</p>
                </Message>
              ) : null}

              {resolveAction === 'manual_review' ? (
                <Message warning>
                  <Message.Header>MercuryCom no puede elegir otra variación automáticamente</Message.Header>
                  <p>La variación vinculada ya no existe, pero Mercado Libre devuelve una o más variaciones diferentes.</p>
                  <p>Revisá la publicación y su SKU en Mercado Libre antes de corregir el vínculo en MercuryCom. No enviaremos stock ni volveremos a consultar el canal desde esta acción.</p>
                </Message>
              ) : null}

              {resolveAction === 'ignore' ? (
                <Message warning>
                  <Message.Header>Ignorar caso</Message.Header>
                  <p>Este caso dejará de aparecer en la vista activa y se archivará para referencia.</p>
                  <p>También evitaremos que vuelva a recrearse mientras la supresión esté vigente.</p>
                </Message>
              ) : null}

              {!selectedIssueUsesStockSourceFlow && resolveAction === 'use_channel_stock_as_mercury' ? (
                <Message warning>
                  <Message.Header>Confirmar cambio de stock central</Message.Header>
                  <p>Vas a cambiar el stock central de MercuryCom usando el stock de {selectedChannelLabel}.</p>
                  <List>
                    <List.Item><strong>SKU:</strong> {selectedIssue.sku || '-'}</List.Item>
                    <List.Item><strong>Stock actual MercuryCom:</strong> {getIssueMercuryStock(selectedIssue)} unidades</List.Item>
                    <List.Item><strong>Stock canal:</strong> {getIssueChannelStock(selectedIssue, selectedIssueContext)} unidades</List.Item>
                    <List.Item><strong>Nuevo stock MercuryCom:</strong> {getIssueChannelStock(selectedIssue, selectedIssueContext)} unidades</List.Item>
                  </List>
                </Message>
              ) : null}

              {!selectedIssueUsesStockSourceFlow && resolveAction === 'update_channel_from_mercury' ? (
                <Message warning>
                  <Message.Header>Confirmar actualización</Message.Header>
                  <p>Vas a actualizar el stock en {selectedChannelLabel} usando MercuryCom como fuente.</p>
                  <List>
                    <List.Item><strong>SKU:</strong> {selectedIssue.sku || '-'}</List.Item>
                    <List.Item><strong>Stock MercuryCom:</strong> {getIssueMercuryStock(selectedIssue)} unidades</List.Item>
                    <List.Item><strong>Stock actual en canal:</strong> {getIssueChannelStock(selectedIssue, selectedIssueContext)} unidades</List.Item>
                    <List.Item><strong>Nuevo stock en canal:</strong> {getIssueMercuryStock(selectedIssue)} unidades</List.Item>
                  </List>
                </Message>
              ) : null}

              <details>
                <summary style={{ cursor: 'pointer', fontWeight: 700 }}>Detalles técnicos</summary>
                <div style={{ marginTop: 8, color: '#4B5563' }}>
                  <div><strong>Primera detección:</strong> {formatDateTime(selectedIssue.firstDetectedAt)}</div>
                  <div><strong>Última detección:</strong> {formatDateTime(selectedIssue.lastDetectedAt)}</div>
                  <div><strong>Ocurrencias:</strong> {selectedIssue.occurrenceCount || 1}</div>
                  <div><strong>Raw error code:</strong> {selectedIssue.rawErrorCode || '-'}</div>
                  <div><strong>Raw error message:</strong> {selectedIssue.rawErrorMessage || '-'}</div>
                </div>
              </details>

              {!selectedIssueUsesStockSourceFlow && confirmRequiredActions.has(resolveAction) ? (
                <Form.Checkbox
                  label={
                    resolveAction === 'update_channel_from_mercury'
                      ? `Confirmo actualizar ${selectedChannelLabel} con el stock de MercuryCom.`
                      : resolveAction === 'use_channel_stock_as_mercury'
                        ? `Confirmo cambiar MercuryCom de ${getIssueMercuryStock(selectedIssue)} a ${getIssueChannelStock(selectedIssue, selectedIssueContext)} unidades.`
                        : 'Confirmo que quiero ejecutar esta acción. Nada se aplica hasta presionar Confirmar.'
                  }
                  checked={resolveConfirmed}
                  onChange={(_, d) => setResolveConfirmed(Boolean(d.checked))}
                />
              ) : null}
            </div>
          ) : null}
        </Modal.Content>
        <Modal.Actions>
          {selectedIssueUsesStockSourceFlow && stockMismatchStep === 'confirm' ? (
            <Button
              basic
              type="button"
              onClick={() => {
                setStockMismatchStep('choose');
                setResolveConfirmed(false);
              }}
            >
              Volver
            </Button>
          ) : (
            <Button basic type="button" onClick={closeResolveModal}>Cancelar</Button>
          )}
          {resolveAction !== 'manual_review' &&
          !(selectedIssueUsesStockSourceFlow && stockMismatchStep === 'choose' && resolveAction !== 'ignore') ? (
            <Button
              color="violet"
              loading={resolving}
              type="button"
              disabled={
                resolving ||
                !resolveAction ||
                (
                  confirmRequiredActions.has(resolveAction) &&
                  !selectedIssueUsesStockSourceFlow &&
                  resolveConfirmed !== true
                )
              }
              onClick={resolveCurrentIssue}
            >
              <Icon name="check" /> {resolveAction === 'update_channel_from_mercury'
                ? `Confirmar y actualizar ${selectedChannelLabel}`
                : resolveAction === 'use_channel_stock_as_mercury'
                  ? 'Confirmar y actualizar MercuryCom'
                  : 'Confirmar'}
            </Button>
          ) : null}
        </Modal.Actions>
      </Modal>

      <Modal
        open={bulkModalOpen}
        onClose={() => setBulkModalOpen(false)}
        size="small"
        closeOnDimmerClick={false}
        closeOnEscape={false}
        scrolling
      >
        <Modal.Header>
          {isBulkStockSourceFlow
            ? bulkAction
              ? selectedBulkActionConfig?.title || bulkStockSourceFlowTitle
              : bulkStockSourceFlowTitle
            : selectedBulkActionConfig?.title || 'Resolución masiva'}
        </Modal.Header>
        <Modal.Content style={{ maxHeight: 'calc(90vh - 160px)', overflowY: 'auto' }}>
          {isBulkStockSourceFlow && !bulkAction ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <div style={{ fontWeight: 800, fontSize: 18 }}>
                  Seleccionaste {selectedRows.length} {bulkStockSourceSelectionLabel}
                </div>
                <div style={{ color: '#4B5563' }}>
                  {bulkStockSourceExplanation}
                </div>
              </div>

              <Segment style={{ ...surfaceCardStyle, padding: 0, overflow: 'hidden' }}>
                <div style={{ padding: '14px 16px', borderBottom: '1px solid #E5E7EB', fontWeight: 800 }}>
                  Diferencias seleccionadas
                </div>
                <div className="responsive-table-container">
                  <Table celled compact="very" style={{ minWidth: 760, margin: 0 }}>
                    <Table.Header>
                      <Table.Row>
                        <Table.HeaderCell>SKU</Table.HeaderCell>
                        <Table.HeaderCell>Publicación</Table.HeaderCell>
                        <Table.HeaderCell>Canal</Table.HeaderCell>
                        <Table.HeaderCell>MercuryCom</Table.HeaderCell>
                        <Table.HeaderCell>Stock actual del canal</Table.HeaderCell>
                      </Table.Row>
                    </Table.Header>
                    <Table.Body>
                      {bulkPreviewRows.map((row) => (
                        <Table.Row key={row.id}>
                          <Table.Cell>{row.sku || '-'}</Table.Cell>
                          <Table.Cell>{row.listingTitle || '-'}</Table.Cell>
                          <Table.Cell>{getChannelLabel(row.channel)}</Table.Cell>
                          <Table.Cell>{toDisplayStock(row.mercuryStock)} unidades</Table.Cell>
                          <Table.Cell>{toDisplayStock(row.channelStock)} unidades</Table.Cell>
                        </Table.Row>
                      ))}
                    </Table.Body>
                  </Table>
                </div>
                {bulkRemainingRows > 0 ? (
                  <div style={{ padding: '10px 16px', color: '#6B7280' }}>
                    y {bulkRemainingRows} más
                  </div>
                ) : null}
              </Segment>

              <div style={{ display: 'grid', gap: 12 }}>
                <Segment style={{ ...surfaceCardStyle, padding: 16, border: '1px solid #DDD6FE', minHeight: 124 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                    <div style={{ fontSize: 18, fontWeight: 800 }}>Usar stock de MercuryCom</div>
                    <Label color="violet" size="tiny">Recomendado</Label>
                  </div>
                  <div style={{ color: '#4B5563', marginBottom: 14 }}>
                    {selectedBulkProblemKey === 'fanout_failed'
                      ? 'Vamos a actualizar los canales seleccionados usando MercuryCom como fuente y volver a sincronizar las publicaciones asociadas.'
                      : 'Vamos a actualizar todos los canales seleccionados para que coincidan con MercuryCom.'}
                  </div>
                  <Button color="violet" onClick={() => {
                    setBulkAction('update_channel_from_mercury');
                    setBulkConfirmed(false);
                  }}>
                    Usar MercuryCom
                  </Button>
                </Segment>

                <Segment
                  style={{
                    ...surfaceCardStyle,
                    padding: 16,
                    border: hasConflictingChannelStocksForBulkMismatch ? '1px solid #FCA5A5' : '1px solid #E5E7EB',
                    background: hasConflictingChannelStocksForBulkMismatch ? '#FEF2F2' : '#FFFFFF',
                    minHeight: 124,
                  }}
                >
                  <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 8 }}>Usar stock del canal</div>
                  <div style={{ color: '#4B5563', marginBottom: 14 }}>
                    {selectedBulkProblemKey === 'fanout_failed'
                      ? 'Vamos a actualizar MercuryCom usando el stock actual del canal en toda la selección. Después sincronizaremos las publicaciones asociadas.'
                      : 'Vamos a actualizar MercuryCom usando el stock actual del canal en toda la selección.'}
                  </div>
                  {hasConflictingChannelStocksForBulkMismatch ? (
                    <Message warning style={{ marginTop: 0 }}>
                      <Message.Header>No podemos usar stock del canal en esta selección.</Message.Header>
                      <p>Hay SKUs con distintos valores entre canales. Resolvé esos casos individualmente para elegir qué stock conservar.</p>
                    </Message>
                  ) : null}
                  <Button
                    color="violet"
                    basic
                    disabled={hasConflictingChannelStocksForBulkMismatch}
                    onClick={() => {
                      setBulkAction('use_channel_stock_as_mercury');
                      setBulkConfirmed(false);
                    }}
                  >
                    Usar stock del canal
                  </Button>
                </Segment>
              </div>
            </div>
          ) : selectedBulkActionConfig ? (
            isBulkStockSourceFlow ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <div style={{ fontWeight: 800, fontSize: 18 }}>
                    {bulkAction === 'update_channel_from_mercury'
                      ? 'Vas a actualizar los canales usando MercuryCom como fuente.'
                      : 'Vas a usar el stock del canal como nuevo stock central de MercuryCom.'}
                  </div>
                  <div style={{ color: '#4B5563' }}>
                    {bulkAction === 'update_channel_from_mercury'
                      ? `Seleccionaste ${selectedRows.length} alertas para resolver usando MercuryCom como fuente.`
                      : `Seleccionaste ${selectedRows.length} alertas para actualizar MercuryCom usando el stock del canal.`}
                  </div>
                </div>

                <Segment style={{ ...surfaceCardStyle, padding: 0, overflow: 'hidden' }}>
                  <div style={{ padding: '14px 16px', borderBottom: '1px solid #E5E7EB', fontWeight: 800 }}>
                    Vista previa del cambio
                  </div>
                  <div className="responsive-table-container">
                    <Table celled compact="very" style={{ minWidth: 760, margin: 0 }}>
                      <Table.Header>
                        <Table.Row>
                          {selectedBulkActionConfig.previewColumns.map((column) => (
                            <Table.HeaderCell key={column.key}>{column.label}</Table.HeaderCell>
                          ))}
                        </Table.Row>
                      </Table.Header>
                      <Table.Body>
                        {bulkPreviewRows.map((row) => (
                          <Table.Row key={row.id}>
                            {selectedBulkActionConfig.previewColumns.map((column) => (
                              <Table.Cell key={`${row.id}-${column.key}`}>{column.render(row)}</Table.Cell>
                            ))}
                          </Table.Row>
                        ))}
                      </Table.Body>
                    </Table>
                  </div>
                  {bulkRemainingRows > 0 ? (
                    <div style={{ padding: '10px 16px', color: '#6B7280' }}>
                      y {bulkRemainingRows} más
                    </div>
                  ) : null}
                </Segment>

                <Message warning style={{ margin: 0 }}>
                  {selectedBulkActionConfig.impact}
                </Message>

                <Form.Checkbox
                  label={selectedBulkActionConfig.confirmLabel(selectedRows.length)}
                  checked={bulkConfirmed}
                  onChange={(_, data) => setBulkConfirmed(Boolean(data.checked))}
                />
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <p>
                  Seleccionaste <strong>{selectedRows.length}</strong> alertas de tipo:{' '}
                  <strong>{selectedBulkActionConfig.problemName || selectedBulkConfig?.problemName}</strong>.
                </p>

                <Message info>
                  <Message.Header>Qué va a pasar</Message.Header>
                  <p>{selectedBulkActionConfig.explanation}</p>
                </Message>

                <div>
                  <div style={{ fontWeight: 800, marginBottom: 8 }}>Vista previa</div>
                  <Table celled compact="very">
                    <Table.Header>
                      <Table.Row>
                        {selectedBulkActionConfig.previewColumns.map((column) => (
                          <Table.HeaderCell key={column.key}>{column.label}</Table.HeaderCell>
                        ))}
                      </Table.Row>
                    </Table.Header>
                    <Table.Body>
                      {bulkPreviewRows.map((row) => (
                        <Table.Row key={row.id}>
                          {selectedBulkActionConfig.previewColumns.map((column) => (
                            <Table.Cell key={`${row.id}-${column.key}`}>{column.render(row)}</Table.Cell>
                          ))}
                        </Table.Row>
                      ))}
                    </Table.Body>
                  </Table>
                  {bulkRemainingRows > 0 ? (
                    <div style={{ color: '#6B7280', marginTop: 8 }}>
                      y {bulkRemainingRows} más
                    </div>
                  ) : null}
                </div>

                <Message warning>
                  <Message.Header>Impacto</Message.Header>
                  <p>{selectedBulkActionConfig.impact}</p>
                </Message>

                <Form.Checkbox
                  label={selectedBulkActionConfig.confirmLabel(selectedRows.length)}
                  checked={bulkConfirmed}
                  onChange={(_, data) => setBulkConfirmed(Boolean(data.checked))}
                />
              </div>
            )
          ) : null}
        </Modal.Content>
        <Modal.Actions>
          {isBulkStockSourceFlow && bulkAction ? (
              <Button
                basic
                type="button"
                onClick={() => {
                  setBulkAction('');
                  setBulkConfirmed(false);
                }}
              >
                Volver
              </Button>
          ) : (
            <Button basic type="button" onClick={() => {
              setBulkModalOpen(false);
              setBulkConfirmed(false);
              setBulkAction('');
            }}>Cancelar</Button>
          )}
          {(!isBulkStockSourceFlow || bulkAction) ? (
            <Button
              color="violet"
              loading={resolvingBulk}
              type="button"
              disabled={resolvingBulk || !selectedBulkActionConfig || bulkConfirmed !== true}
              onClick={runBulkAction}
            >
              {selectedBulkActionConfig?.cta || 'Confirmar'}
            </Button>
          ) : null}
        </Modal.Actions>
      </Modal>
    </Container>
  );
}

export default StockInconsistenciesDashboard;

